import type { Lazy, Predicate, Refinement } from 'fp-ts/lib/function';

import { isNonNullable } from '../types/isNonNullable';
import { isNullable } from '../types/isNullable';

export type Nullable<A> = A | null | undefined;

const fromEmptyString = <A>(a: A): A | null => (a === '' ? null : (a ?? null));

function fromPredicate<A, B extends A>(
  refinement: Refinement<A, B>,
): (a: A) => B | null;
function fromPredicate<A>(predicate: Predicate<A>): (a: A) => A | null;
function fromPredicate<A>(predicate: Predicate<A>): (a: A) => A | null {
  return (a) => (predicate(a) ? a : null);
}

const chain =
  <A, B>(f: (a: A) => Nullable<B>) =>
  (fa: Nullable<A>): B | null =>
    isNonNullable(fa) ? (f(fa) ?? null) : null;

const chainPredicate: {
  <A, B extends A>(refinement: Refinement<A, B>): (a: A | null) => B | null;
  <A>(predicate: Predicate<A>): (a: A | null) => A | null;
} = (predicate: Predicate<unknown>) => chain(fromPredicate(predicate));

const fold =
  <A, B>(onNone: Lazy<B>, onSome: (a: A) => B) =>
  (ma: Nullable<A>): B =>
    isNullable(ma) ? onNone() : onSome(ma);

const getOrElse =
  <A>(onNullable: Lazy<A>) =>
  (fa: Nullable<A>): A =>
    fa ?? onNullable();

/**
 * Basically same as `chain` in case of nullable since it is not actually a wrapped type,
 * but let's use map for better semantics whe we don't intend to return null from the
 * mapping function.
 */
const map =
  <A, B>(f: (a: A) => NonNullable<B>) =>
  (fa: Nullable<A>): B | null =>
    isNonNullable(fa) ? f(fa) : null;

/**
 * Lightweight replacement of Option type
 *
 * As we don't use Option so extensively and typescript can decently typecheck nullable types,
 * this monad offers a more concise way of piping nullable values without frequent un/wrapping
 * optional containers.
 */
export const nullable = {
  chain,
  chainPredicate,
  fold,
  fromEmptyString,
  fromPredicate,
  getOrElse,
  map,
};
